import {
  IJourneyLeg,
  IJourneyTrip,
  IOnDemandConnectionOption,
} from '@web/dtos';

export interface IOnDemandPublicTransportConnectionLimits {
  earliestOnDemandDeparture: number | undefined;
  latestOnDemandArrival: number | undefined;
}

export function getOnDemandPublicTransportConnectionLimits(
  trip: IJourneyTrip | undefined,
  onDemandLeg: IJourneyLeg | undefined
): IOnDemandPublicTransportConnectionLimits {
  if (!trip || !onDemandLeg) {
    return {
      earliestOnDemandDeparture: undefined,
      latestOnDemandArrival: undefined,
    };
  }

  const onDemandLegIndex = trip.legs.findIndex(
    leg => leg === onDemandLeg || leg.id === onDemandLeg.id
  );
  if (onDemandLegIndex < 0) {
    return {
      earliestOnDemandDeparture: undefined,
      latestOnDemandArrival: undefined,
    };
  }

  const previousPublicTransportLegIndex = findPreviousPublicTransportLegIndex(
    trip.legs,
    onDemandLegIndex,
    onDemandLeg
  );
  const nextPublicTransportLegIndex = findNextPublicTransportLegIndex(
    trip.legs,
    onDemandLegIndex,
    onDemandLeg
  );

  const earliestOnDemandDeparture =
    previousPublicTransportLegIndex >= 0
      ? parseJourneyTime(trip.legs[previousPublicTransportLegIndex].destination)
      : undefined;

  const latestOnDemandArrival =
    nextPublicTransportLegIndex >= 0
      ? parseJourneyTime(trip.legs[nextPublicTransportLegIndex].origin)
      : undefined;

  return { earliestOnDemandDeparture, latestOnDemandArrival };
}

export function filterConnectionOptionsByPublicTransportConnections(
  connectionOptions: IOnDemandConnectionOption[],
  trip: IJourneyTrip | undefined,
  onDemandLeg: IJourneyLeg | undefined
): IOnDemandConnectionOption[] {
  if (!trip || !onDemandLeg) {
    return connectionOptions;
  }

  const { earliestOnDemandDeparture, latestOnDemandArrival } =
    getOnDemandPublicTransportConnectionLimits(trip, onDemandLeg);

  if (
    earliestOnDemandDeparture === undefined &&
    latestOnDemandArrival === undefined
  ) {
    return connectionOptions;
  }

  return connectionOptions.filter(option => {
    const departure = Date.parse(option.departureTimetabled);
    const arrival = Date.parse(option.arrivalTimetabled);

    return (
      (earliestOnDemandDeparture === undefined ||
        (Number.isFinite(earliestOnDemandDeparture) &&
          Number.isFinite(departure) &&
          departure >= earliestOnDemandDeparture)) &&
      (latestOnDemandArrival === undefined ||
        (Number.isFinite(latestOnDemandArrival) &&
          Number.isFinite(arrival) &&
          arrival <= latestOnDemandArrival))
    );
  });
}

export function deduplicateConnectionOptions(
  connectionOptions: IOnDemandConnectionOption[]
): IOnDemandConnectionOption[] {
  const uniqueOptions = new Map<string, IOnDemandConnectionOption>();

  connectionOptions.forEach(option => {
    const key = [
      option.publicTransportXml,
      option.departureTimetabled,
      option.arrivalTimetabled,
    ].join('\u0000');
    if (!uniqueOptions.has(key)) {
      uniqueOptions.set(key, option);
    }
  });

  return [...uniqueOptions.values()];
}

export function resolveConnectionOptionSelection(
  connectionOptions: IOnDemandConnectionOption[],
  onDemandLeg: IJourneyLeg | undefined,
  currentSelection: IOnDemandConnectionOption | null
): IOnDemandConnectionOption | undefined {
  if (!connectionOptions.length || !onDemandLeg) {
    return undefined;
  }

  const currentOption = currentSelection
    ? connectionOptions.find(
        option =>
          option.publicTransportXml === currentSelection.publicTransportXml &&
          option.departureTimetabled === currentSelection.departureTimetabled &&
          option.arrivalTimetabled === currentSelection.arrivalTimetabled
      )
    : undefined;
  if (currentOption) {
    return currentOption;
  }

  const plannedDeparture = parseJourneyTime(onDemandLeg.origin);
  if (!Number.isFinite(plannedDeparture)) {
    return connectionOptions[0];
  }

  const matchingOption = connectionOptions.find(
    option => Date.parse(option.departureTimetabled) >= plannedDeparture
  );
  if (matchingOption) {
    return matchingOption;
  }

  return connectionOptions[connectionOptions.length - 1];
}

function findPreviousPublicTransportLegIndex(
  legs: IJourneyLeg[],
  onDemandLegIndex: number,
  onDemandLeg: IJourneyLeg
): number {
  for (let index = onDemandLegIndex - 1; index >= 0; index--) {
    if (isPublicTransportLeg(legs[index], onDemandLeg)) {
      return index;
    }
  }
  return -1;
}

function findNextPublicTransportLegIndex(
  legs: IJourneyLeg[],
  onDemandLegIndex: number,
  onDemandLeg: IJourneyLeg
): number {
  for (let index = onDemandLegIndex + 1; index < legs.length; index++) {
    if (isPublicTransportLeg(legs[index], onDemandLeg)) {
      return index;
    }
  }
  return -1;
}

function isPublicTransportLeg(
  leg: IJourneyLeg,
  onDemandLeg: IJourneyLeg
): boolean {
  return leg.type === 'JNY' && leg.operator.id !== onDemandLeg.operator.id;
}

function parseJourneyTime(location: IJourneyLeg['origin']): number {
  return Date.parse(`${location.date}T${location.time}`);
}
